import { Box } from '@mui/material'
import ImageEditor from '@toast-ui/react-image-editor'
import axios from 'axios'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import '@/assets/tui-image-editor.css'
import { useAppSelector } from '@/hooks/redux'
import {
  useLazyGetCaseDetailsQuery,
  useLazyWithouteditImageQuery,
  useUpdateRecipeImageMutation,
} from '@/store/apiService'
import {
  setCanvasImage,
  setDualCanvasImage,
  setImageEdit,
} from '@/store/appSlice'
import { DUAL_CANVAS_INCLUDED_PATHS } from '@/utils/constants'
import { getDehazedImageUrl } from '@/utils/helper'

import Button from '../Button'

type ImageEditComponentEntity = {
  selectedImage: any
}

function ImageEdit({ selectedImage }: ImageEditComponentEntity) {
  const { t } = useTranslation('translation', { keyPrefix: 'imageEdit' })

  const imageEditorRef = useRef<any>(null)
  const dispatch = useDispatch()
  const location = useLocation()
  const path = location.pathname

  const placement = useAppSelector((state) => state.app.placement)
  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)
  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const caseId = new URLSearchParams(location.search).get('caseId') || ''

  const [getWithoutEditImage] = useLazyWithouteditImageQuery()
  const [updateRecipeImage] = useUpdateRecipeImageMutation()
  const [triggerGetCaseDetails] = useLazyGetCaseDetailsQuery()

  const isDualCanvas = useMemo(
    () => DUAL_CANVAS_INCLUDED_PATHS?.includes(path),
    [path],
  )

  const dataURLtoFile = (dataurl: string, filename: string) => {
    const arr = dataurl.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1] || ''
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
  }

  const createEditedImageFile = () => {
    const dataUrl = imageEditorRef.current.getInstance().toDataURL()
    return dataURLtoFile(dataUrl, 'image.png')
  }

  const uploadEditedImageAndUpdateRecipe = async () => {
    const imgFile = createEditedImageFile()

    const response = await getWithoutEditImage(imgFile.name).unwrap()

    await axios.put((response as any)?.url, imgFile, {
      headers: { platform: 'web' },
    })

    const updatedResponse = await updateRecipeImage({
      imageId: selectedImage?.id,
      key: (response as any).key,
      note: '',
    } as any).unwrap()

    return updatedResponse
  }

  const updateCanvasState = (updatedResponse: any) => {
    if (!isDualCanvas) {
      dispatch(setCanvasImage(updatedResponse))

      if (dualCanvasImage?.left || dualCanvasImage?.right) {
        const updatedDualCanvas = {
          left:
            dualCanvasImage?.left?.id === canvasImage.id
              ? updatedResponse
              : dualCanvasImage.left,
          right:
            dualCanvasImage?.right?.id === canvasImage.id
              ? updatedResponse
              : dualCanvasImage.right,
        }

        dispatch(setDualCanvasImage(updatedDualCanvas))
      }

      return
    }

    // Dual canvas mode
    dispatch(setDualCanvasImage({ [placement as any]: updatedResponse }))

    if (
      canvasImage &&
      (dualCanvasImage as any)[placement as any]?.id === canvasImage?.id
    ) {
      dispatch(setCanvasImage(updatedResponse))
    }
  }

  const onImageEdit = async () => {
    try {
      toast.info(t('editStarted'))
      dispatch(
        setImageEdit({
          imageEdit: false,
          placement: undefined,
          path,
        }),
      )

      const updatedResponse = await uploadEditedImageAndUpdateRecipe()

      updateCanvasState(updatedResponse)

      await triggerGetCaseDetails({ caseId })

      toast.success(t('editCompleted'))
    } catch (error) {
      toast.error(t('editFailed'))
    }
  }

  return (
    <Box
      style={{
        height: 'calc(100% - 25px)',
        position: 'relative',
        flexGrow: 1,
        display: 'flex',
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          display: 'flex',
          justifyContent: 'flex-end',
          zIndex: 10,
        }}
      >
        <Button
          label={t('saveChanges')}
          onClick={onImageEdit}
          variant="contained"
          sx={{
            background: '#90CAF9',
            boxShadow:
              '0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.20)',
            color: 'black',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '24px',
          }}
          textTransform="uppercase"
        />
      </Box>

      <ImageEditor
        ref={imageEditorRef}
        id="myeditor"
        includeUI={{
          loadImage: {
            path:
              getDehazedImageUrl(selectedImage) ||
              selectedImage?.latest_image ||
              selectedImage?.calibrated_image ||
              selectedImage?.image,
            name: 'SampleImage',
          },
          theme: {
            'header.display': 'none',
          },
          menu: ['crop', 'rotate'],
          uiSize: {
            width: '100%',
            height: '100%',
          },
          menuBarPosition: 'bottom',
        }}
        cssMaxHeight={500}
        cssMaxWidth={700}
        selectionStyle={{
          cornerSize: 20,
          rotatingPointOffset: 70,
        }}
        usageStatistics={false}
      />
    </Box>
  )
}

export default ImageEdit
